import { PrinterOutlined } from '@ant-design/icons';
import { useGetDetailInstitution } from '@vindr/services/actions/institution.action';
import { useGetApprover, useGetListReport } from '@vindr/services/actions/report.action';
import { ResponseGetLayouts } from '@vindr/services/apis/layout.api';
import { ResponseGetOrders } from '@vindr/services/apis/order.api';
import { ResponseGetReports } from '@vindr/services/apis/report.api';
import { ResponseFetchStudyList } from '@vindr/services/apis/study.api';
import { GENDER } from '@vindr/services/constants/common.constant';
import { REPORT_VARIABLES } from '@vindr/services/constants/report.constant';
import { convertPhoneNumber } from '@vindr/utils/formatText/formatText';
import { getDateFormatted } from '@vindr/utils/helpers/common.helper';
import { generateQRCode } from '@vindr/utils/helpers/qrcode';
import {
  generateShareLink,
  generateVisitLink,
  getAccessKeys,
} from '@vindr/utils/report/generateLink';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UseTranslationResponse, useTranslation } from 'react-i18next';
import './Print.css';
import Converter from '@vindr/utils/unit-converter';

const convertUnits = new Converter('px', 'mm', 'inch').convertUnits;

const MARGIN_VERTICAL_PAGE = 5; // 5mm
const MARGIN_HORIZONTAL_PAGE = 10; // 10mm

export default function PrintModal({
  report,
  order,
  layout,
  study,
  reports,
  handleClose,
}: {
  report?: ResponseGetReports['data'][0];
  order?: ResponseGetOrders['data'][0];
  layout?: ResponseGetLayouts['data'][0];
  study?: ResponseFetchStudyList['data'][0];
  reports?: ReturnType<typeof useGetListReport>[number]['data'][];
  handleClose: (e) => void;
}) {
  const { data: institutionResponse } = useGetDetailInstitution(order?.institution_id, {
    enabled: !reports?.length,
  });

  const institution = institutionResponse?.data;
  const approver = useGetApprover({ id: report?.id }, { enabled: !reports?.length })?.data?.data;

  const { t } = useTranslation('Vindoc');
  const { t: tReport } = useTranslation('Report');
  const componentRef = useRef();

  /*   const preview = useMemo(() => {
    if (!reports?.length) {
      return getReportPreview(
        report,
        order,
        layout,
        study,
        institution,
        approver,
        qrBase64,
        tReport
      );
    }

    return reports
      .map((it) => {
        const urlViewer = generateShareLink(
          it?.study?.study_instance_uid,
          it.recentReport?.share_access_key
        );

        return getReportPreview(
          it.recentReport,
          it.recentOrder,
          null,
          study,
          it.institutionDetail,
          it.approver,
          qrBase64,
          tReport
        );
      })
      .join('<div style="page-break-after:always"></div>');
  }, [approver, institution, layout, order, qrBase64, report, reports, study, tReport]); */

  const [preview, setPreview] = useState('');

  const createPreview = useCallback(async () => {
    if (!reports?.length) {
      const { study_access_key, visit_access_key } = getAccessKeys(report?.share_access_keys) || {};

      const shareLink = generateShareLink(
        study_access_key?.study_instance_uid || study?.study_instance_uid,
        study_access_key?.key,
        study?.modality || study?.modalities
      );
      const qrBase64 = shareLink ? await generateQRCode(shareLink) : '';

      const visitLink = generateVisitLink({
        visit_id: visit_access_key?.visit_id || study?.visit_id,
        access_key: visit_access_key?.key,
      });
      const qrVisitID = visitLink ? await generateQRCode(visitLink) : '';

      return setPreview(
        getReportPreview(
          report,
          order,
          layout,
          study,
          institution,
          approver,
          qrBase64,
          tReport,
          qrVisitID
        )
      );
    }

    const tempPreview = await Promise.all(
      reports.map(async (it) => {
        const { study_access_key, visit_access_key } =
          getAccessKeys(it?.recentReport?.share_access_keys) || {};

        const shareLink = generateShareLink(
          study_access_key?.study_instance_uid || it?.study?.study_instance_uid,
          study_access_key?.key,
          it?.study?.modality
        );
        const qrBase64 = shareLink ? await generateQRCode(shareLink) : '';

        const visitLink = generateVisitLink({
          visit_id: visit_access_key?.visit_id || it?.study?.visit_id,
          access_key: visit_access_key?.key,
        });
        const qrVisitID = visitLink ? await generateQRCode(visitLink) : '';

        return getReportPreview(
          it.recentReport,
          it.recentOrder,
          null,
          it.study,
          it.institutionDetail,
          it.approver,
          qrBase64,
          tReport,
          qrVisitID
        );
      })
    );

    setPreview(tempPreview.join('<div class="break-page"></div>'));
  }, [approver, institution, layout, order, report, reports, study, tReport]);

  useEffect(() => {
    createPreview();
  }, [createPreview]);

  /*   const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      try {
      } catch (error) {
        console.error('hide border failed:', error);
      }
    },
    onAfterPrint: () => {
      try {
      } catch (error) {
        console.log('hide border failed:', error);
      }
    },
    pageStyle,
  }); */

  /*   const handlePrint = useCallback(() => {
    const iframe: HTMLIFrameElement = componentRef.current;

    if (!iframe) return;
    const contentWindow = iframe.contentWindow;
    contentWindow.focus(); //? IE fix
    contentWindow.print();
  }, []);
 */
  const handlePrint = useCallback(() => {
    const iframe: HTMLIFrameElement = componentRef.current;
    if (!iframe) return;

    const contentDocument = iframe.contentDocument;
    const contentWindow = iframe.contentWindow;
    const body = contentDocument.body;
    const html = contentDocument.documentElement;
    const heightPrintContent = convertUnits(
      Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      ),
      'px',
      'mm'
    );

    const [A4_WIDTH_MM, A4_HEIGHT_MM] = [210, 297];
    const footer = contentDocument.querySelector('tfoot');
    const printElement = footer.closest('table');

    contentWindow.addEventListener('beforeprint', (e) => {
      if (!printElement) return;

      printElement.classList.add('temp-class-for-height');
      const footerHeight = convertUnits(footer?.clientHeight ?? 0, 'px', 'mm');
      const printArea = A4_HEIGHT_MM - MARGIN_VERTICAL_PAGE * 2; // 287mm
      const useableHeight = printArea - footerHeight;
      const numberOfPages = Math.ceil((heightPrintContent - footerHeight) / useableHeight);

      /* const heightWithSingleHeader = numberOfPage * PAGE_HEIGHT;
        let requiredHeight = heightWithSingleHeader;
        if (numberOfPage > 1) {
          const headerHeight = header?.clientHeight ?? 0;
          const footerHeight = footer?.clientHeight ?? 0;
          requiredHeight -= (numberOfPage - 1) * (headerHeight + footerHeight);
        }
        printElement.style.height = `${requiredHeight}px`; */

      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

      let currentPage = 1;
      while (currentPage <= numberOfPages) {
        const pageCount = iframeDocument.createElement('div');
        const divHeight = 5; //5mm
        const baseBottomPosition = printArea - divHeight; //282mm
        const topPosition = baseBottomPosition + (currentPage - 1) * printArea;

        pageCount.innerHTML = `<div class="count-page" style="position: absolute; top: ${topPosition}mm; right: 0; height:${divHeight}mm">${currentPage}/${numberOfPages}</div>`;
        iframeDocument.body.appendChild(pageCount);

        currentPage++;
      }
    });

    contentWindow.addEventListener('afterprint', () => {
      contentDocument.querySelectorAll('.count-page').forEach((x) => x.remove());
    });

    contentWindow.focus(); //? IE fix
    contentWindow.print();

    if (printElement) {
      printElement.classList.remove('temp-class-for-height');
      printElement.style.height = `auto`;
    }
  }, []);

  return (
    <Modal
      open={true}
      style={{
        backgroundColor: '#fff',
        color: '#000',
        paddingBottom: 0,
        top: 12,
      }}
      width={785}
      zIndex={1010}
      closable={false}
      aria-labelledby="max-width-dialog-title"
      className="printing-dialog"
      okText={
        <>
          <PrinterOutlined /> {t('Print')}
        </>
      }
      onOk={handlePrint}
      onCancel={handleClose}
      cancelText={<div className="text-black">{t('Cancel')}</div>}
    >
      <div className="preview-content" style={{ position: 'relative' }}>
        {/* <div dangerouslySetInnerHTML={{ __html: preview }} /> */}
        <iframe
          ref={componentRef}
          style={{
            border: 0,
            overflow: 'hidden',
            height: '85vh',
            width: `100%`,
          }}
          srcDoc={`
            <html style="width: calc(210mm - ${MARGIN_HORIZONTAL_PAGE * 2}mm); margin: 0;">
              <head>
                <style>${pageStyle}</style>
              </head>
              <body>
              ${preview}
              <script>
                ${setFooterSpace}
              </script>
              <script>
                ${removePage}
              </script>
              </body>
            </html>
          `}
        />
      </div>
    </Modal>
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function getReportPreview(
  report: ResponseGetReports['data'][0],
  order: ResponseGetOrders['data'][0],
  layout: ResponseGetLayouts['data'][0],
  study: ResponseFetchStudyList['data'][0],
  institution: ReturnType<typeof useGetDetailInstitution>['data']['data'],
  approver: ResponseGetReports['data'][0]['approver'],
  qrBase64 = null,
  tReport: UseTranslationResponse<string, any>['t'],
  qrVisitID = null
) {
  const { study_date: StudyDateTime } = study || {};

  const {
    content: ReportContent,
    conclusion: ReportConclusion,
    updated_at: ReportDateTime,
    submitter,
    layout: ReportLayout,
    procedure,
    share_access_keys,
  } = report || {};
  const { visit_access_key } = getAccessKeys(share_access_keys) || {};

  const reportApprover = approver || report?.approver;

  const Approver = reportApprover && `${reportApprover?.title || ''}${approver?.fullname}`;
  const Submitter = submitter && `${submitter?.title || ''}${submitter?.fullname}`;
  const Signature = reportApprover?.signature;

  const {
    logo: InstitutionLogo,
    name: InstitutionName,
    address: InstitutionAddress,
    phone: InstitutionPhone,
    email: InstitutionEmail,
  } = institution || {};

  const ProcedureName = procedure?.name;

  const {
    order_doctor_name: OrderDoctorName,
    refer_doctor_name: ReferDoctorName,
    diagnostic: Diagnosis,
    visited_patient: patient,
  } = order || {};

  const {
    patient_id: PatientID,
    visit_id: VisitID,
    name: PatientName,
    birth_date: PatientBirthday,
    age: PatientAge,
    sex: PatientSex,
    address: PatientAddress,
    phone: PatientPhone,
  } = patient || {};

  const layoutContent = ReportLayout?.content || layout?.content;

  // const { signature: Signature } = userInfo || {}; // using signature of approver

  if (!layoutContent) return '';

  const FORMAT_DATE = 'DD/MM/YYYY HH:mm:ss';
  const PrintDateTime = dayjs().format(FORMAT_DATE);

  const layoutConverter = [
    [
      REPORT_VARIABLES.StudyDateTime.code,
      StudyDateTime ? dayjs(StudyDateTime).format(FORMAT_DATE) : '',
    ],
    [REPORT_VARIABLES.ReportContent.code, ReportContent || ''],
    [REPORT_VARIABLES.ReportConclusion.code, ReportConclusion || ''],
    [
      REPORT_VARIABLES.ReportDateTime.code,
      ReportDateTime ? dayjs(ReportDateTime).format(FORMAT_DATE) : '',
    ],
    [REPORT_VARIABLES.Approver.code, Approver || ''],
    [REPORT_VARIABLES.Submitter.code, Submitter || ''],
    [
      REPORT_VARIABLES.InstitutionLogo.code,
      InstitutionLogo
        ? `<img style="max-height: 100px; margin: 0 auto; padding: 2px" src=${InstitutionLogo} />`
        : '',
    ],
    [REPORT_VARIABLES.InstitutionName.code, InstitutionName || ''],
    [REPORT_VARIABLES.InstitutionAddress.code, InstitutionAddress || ''],
    [REPORT_VARIABLES.InstitutionPhone.code, InstitutionPhone || ''],
    [REPORT_VARIABLES.InstitutionEmail.code, InstitutionEmail || ''],
    [REPORT_VARIABLES.ProcedureName.code, ProcedureName || ''],
    [REPORT_VARIABLES.OrderDoctorName.code, OrderDoctorName || ''],
    [REPORT_VARIABLES.ReferDoctorName.code, ReferDoctorName || ''],
    [REPORT_VARIABLES.Diagnosis.code, Diagnosis || ''],
    [REPORT_VARIABLES.PatientID.code, PatientID || ''],
    [REPORT_VARIABLES.VisitID.code, VisitID || ''],
    [REPORT_VARIABLES.PatientName.code, PatientName || ''],
    [
      REPORT_VARIABLES.PatientBirthday.code,
      PatientBirthday ? dayjs(PatientBirthday).format('DD/MM/YYYY') : '',
    ],
    [REPORT_VARIABLES.PatientAge.code, PatientAge ?? ''],
    [
      REPORT_VARIABLES.PatientSex.code,
      PatientSex || PatientSex === 0 ? tReport(GENDER[PatientSex]) : '',
    ],
    [REPORT_VARIABLES.PatientAddress.code, PatientAddress || ''],
    [REPORT_VARIABLES.PatientPhone.code, convertPhoneNumber(PatientPhone)],
    [REPORT_VARIABLES.PrintDateTime.code, PrintDateTime || ''],
    [
      REPORT_VARIABLES.Signature.code,
      Signature
        ? `<img style="max-height: 100px; margin: 0 auto; padding: 2px" src=${Signature} />`
        : '',
    ],
    [REPORT_VARIABLES.QRCode.code, qrBase64 ? `<img style="padding: 2px" src=${qrBase64} />` : ''],
    [
      REPORT_VARIABLES.Password.code,
      PatientPhone ? `${PatientPhone.substring(PatientPhone.length - 4)}a@` : '',
    ],
    [REPORT_VARIABLES.Today.code, dayjs().format(getDateFormatted())],
    [
      REPORT_VARIABLES.PatientBirthdayYear.code,
      PatientBirthday ? dayjs(PatientBirthday).format('YYYY') : '',
    ],
    [
      REPORT_VARIABLES.QRVisit.code,
      qrVisitID ? `<img style="padding: 2px" src=${qrVisitID} />` : '',
    ],
    [REPORT_VARIABLES.QRVisitPassword.code, visit_access_key?.password || ''],
  ];

  return layoutConverter.reduce((acc, [key, value]) => {
    return acc.replaceAll(key, value);
  }, layoutContent);
}

const removePage = `
function contains(selector, text) {
  var elements = document.querySelectorAll(selector);
  return Array.prototype.filter.call(elements, function(element){
    return RegExp(text).test(element.innerText);
  });
}
contains('p', /Trang.+[0-9]/).forEach(x => x.textContent = '')
`;

const setFooterSpace = `
const footer = document.querySelector('.page-footer');
const footerSpace = document.querySelector('.page-footer-space');
const tr = footerSpace.closest('tr');
if (footer && footerSpace) {
  footerSpace.style.height = footer.clientHeight + 'px';
  tr.style.height = footer.clientHeight + 'px';
}
`;

const pageStyle = `
    @page {
      size: 210mm 297mm;
      margin: ${MARGIN_VERTICAL_PAGE}mm ${MARGIN_HORIZONTAL_PAGE}mm;
    }
    body {
      background: #fff;
      font-family: Times New Roman, Times, serif !important;
      color: #000 !important;
      height: max-content;
      padding: 0;
      margin: 0;
    }
    strong,
    em,
    p,
    span {
      font-size: 1em;
    }
    .signature {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .wrapper .ant-upload-picture-card-wrapper {
      width: auto !important;
    }
    .ant-upload.ant-upload-select {
      border: none !important;
    }
    .ant-upload.ant-upload-select-picture-card {
      background: transparent !important;
    }
    .signature-text {
      display: none !important;
    }
    .tb-edit-text {
      border: none !important;
    }
    ul li {
      list-style-type: disc;
    }
    ol li {
      list-style-type: decimal;
    }
    li {
      display: list-item;
    }
    img {
      max-width: 100%;
    }
    /* Hidden Caregiver */
    .standard {
      display: none;
    }

    .page-footer {
      position: fixed;
      bottom: 0;
    }

    .page-header {
      position: fixed;
      top: 0mm;
    }

    tfoot td, thead td {
      padding: 0 !important;
    }

    @media print {
      thead {
        display: table-header-group;
      }
      tfoot {
        display: table-footer-group;
      }

      button {
        display: none;
      }

      body {
        margin: 0;
      }
    }
  `;

const removeHeaderFooterPrint = `
  @media print {
    @page { margin: 0; }
    body { margin: 1.6cm; }
  }
`;
